/**
 * Input Security and Sanitization Utilities
 * Provides protection against XSS, injection attacks, and invalid inputs
 */

/**
 * HTML sanitization utility
 */
export const sanitizeHtml = (input) => {
  if (typeof input !== 'string') return input;
  
  // Basic HTML entity encoding
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * SQL injection protection (for display/logging - backend should handle actual SQL security)
 */
export const sanitizeSqlInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove or escape potentially dangerous SQL characters
  return input
    .replace(/['";\\]/g, '') // Remove quotes and backslashes
    .replace(/--/g, '') // Remove SQL comment syntax
    .replace(/\/\*/g, '') // Remove block comment start
    .replace(/\*\//g, '') // Remove block comment end
    .replace(/\bUNION\b/gi, '') // Remove UNION statements
    .replace(/\bSELECT\b/gi, '') // Remove SELECT statements
    .replace(/\bINSERT\b/gi, '') // Remove INSERT statements
    .replace(/\bUPDATE\b/gi, '') // Remove UPDATE statements
    .replace(/\bDELETE\b/gi, '') // Remove DELETE statements
    .replace(/\bDROP\b/gi, ''); // Remove DROP statements
};

/**
 * Email validation and sanitization
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, sanitized: '', error: 'Email is required' };
  }
  
  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sanitized = email.trim().toLowerCase();
  
  if (!emailRegex.test(sanitized)) {
    return { isValid: false, sanitized, error: 'Invalid email format' };
  }
  
  if (sanitized.length > 254) {
    return { isValid: false, sanitized, error: 'Email too long' };
  }
  
  return { isValid: true, sanitized, error: null };
};

/**
 * Search query sanitization
 */
export const sanitizeSearchQuery = (query) => {
  if (!query || typeof query !== 'string') return '';
  
  return query
    .trim()
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/['"]/g, '') // Remove quotes
    .replace(/javascript:/gi, '') // Remove javascript protocol
    .replace(/data:/gi, '') // Remove data protocol
    .replace(/vbscript:/gi, '') // Remove vbscript protocol
    .slice(0, 100); // Limit length
};

/**
 * Numeric input validation and sanitization
 */
export const validateNumeric = (value, options = {}) => {
  const {
    min = -Infinity,
    max = Infinity,
    isInteger = false,
    allowNegative = true,
  } = options;
  
  if (value === null || value === undefined || value === '') {
    return { isValid: false, sanitized: null, error: 'Value is required' };
  }
  
  let numeric = parseFloat(value);
  
  if (isNaN(numeric)) {
    return { isValid: false, sanitized: null, error: 'Value must be a number' };
  }
  
  if (isInteger && !Number.isInteger(numeric)) {
    return { isValid: false, sanitized: null, error: 'Value must be a whole number' };
  }
  
  if (!allowNegative && numeric < 0) {
    return { isValid: false, sanitized: null, error: 'Value cannot be negative' };
  }
  
  if (numeric < min) {
    return { isValid: false, sanitized: null, error: `Value must be at least ${min}` };
  }
  
  if (numeric > max) {
    return { isValid: false, sanitized: null, error: `Value cannot exceed ${max}` };
  }
  
  return { isValid: true, sanitized: numeric, error: null };
};

/**
 * URL validation and sanitization
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return { isValid: false, sanitized: '', error: 'URL is required' };
  }
  
  const sanitized = url.trim();
  
  // Basic URL validation
  try {
    const urlObj = new URL(sanitized);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { isValid: false, sanitized, error: 'Only HTTP and HTTPS URLs are allowed' };
    }
    
    return { isValid: true, sanitized: urlObj.toString(), error: null };
  } catch (error) {
    return { isValid: false, sanitized, error: 'Invalid URL format' };
  }
};

/**
 * Generic text input sanitization
 */
export const sanitizeTextInput = (input, options = {}) => {
  if (!input || typeof input !== 'string') return '';
  
  const {
    maxLength = 1000,
    allowHtml = false,
    allowSpecialChars = true,
  } = options;
  
  let sanitized = input.trim();
  
  // HTML sanitization
  if (!allowHtml) {
    sanitized = sanitizeHtml(sanitized);
  }
  
  // Remove special characters if not allowed
  if (!allowSpecialChars) {
    sanitized = sanitized.replace(/[^\w\s.-]/g, '');
  }
  
  // Length limit
  if (sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength);
  }
  
  return sanitized;
};

/**
 * Password validation
 */
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password is required', strength: 0 };
  }
  
  const errors = [];
  let strength = 0;
  
  // Length check
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  } else {
    strength += 20;
  }
  
  // Uppercase check
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  } else {
    strength += 20;
  }
  
  // Lowercase check
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  } else {
    strength += 20;
  }
  
  // Number check
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  } else {
    strength += 20;
  }
  
  // Special character check
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  } else {
    strength += 20;
  }
  
  // Length bonus
  if (password.length >= 12) {
    strength += 10;
  }
  
  return {
    isValid: errors.length === 0,
    error: errors.join('. '),
    strength: Math.min(strength, 100),
  };
};

/**
 * Form data sanitization utility
 */
export const sanitizeFormData = (formData, schema = {}) => {
  const sanitized = {};
  const errors = {};
  
  Object.keys(formData).forEach(key => {
    const value = formData[key];
    const fieldSchema = schema[key] || {};
    const { type = 'text', required = false, ...options } = fieldSchema;
    
    // Handle empty values
    if (!value && value !== 0) {
      if (required) {
        errors[key] = `${key} is required`;
      }
      sanitized[key] = null;
      return;
    }
    
    // Sanitize based on type
    switch (type) {
      case 'email':
        const emailResult = validateEmail(value);
        sanitized[key] = emailResult.sanitized;
        if (!emailResult.isValid) {
          errors[key] = emailResult.error;
        }
        break;
        
      case 'number':
        const numResult = validateNumeric(value, options);
        sanitized[key] = numResult.sanitized;
        if (!numResult.isValid) {
          errors[key] = numResult.error;
        }
        break;
        
      case 'url':
        const urlResult = validateUrl(value);
        sanitized[key] = urlResult.sanitized;
        if (!urlResult.isValid) {
          errors[key] = urlResult.error;
        }
        break;
        
      case 'password':
        const passwordResult = validatePassword(value);
        sanitized[key] = value; // Don't modify password, just validate
        if (!passwordResult.isValid) {
          errors[key] = passwordResult.error;
        }
        break;
        
      case 'search':
        sanitized[key] = sanitizeSearchQuery(value);
        break;
        
      default:
        sanitized[key] = sanitizeTextInput(value, options);
        break;
    }
  });
  
  return {
    data: sanitized,
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};

/**
 * Security headers validation for API responses
 */
export const validateSecurityHeaders = (headers) => {
  const requiredHeaders = [
    'X-Content-Type-Options',
    'X-Frame-Options',
    'X-XSS-Protection',
  ];
  
  const missingHeaders = requiredHeaders.filter(header => 
    !headers.has?.(header) && !headers[header]
  );
  
  if (missingHeaders.length > 0) {
    console.warn('Missing security headers:', missingHeaders);
  }
  
  return missingHeaders.length === 0;
};

/**
 * Content Security Policy validation
 */
export const validateCSP = (cspHeader) => {
  if (!cspHeader) {
    console.warn('No Content Security Policy header found');
    return false;
  }
  
  const requiredDirectives = ['default-src', 'script-src', 'style-src'];
  const hasRequired = requiredDirectives.some(directive => 
    cspHeader.includes(directive)
  );
  
  if (!hasRequired) {
    console.warn('CSP header missing required directives');
  }
  
  return hasRequired;
};

/**
 * Rate limiting helper for client-side throttling
 */
export const createRateLimiter = (maxRequests = 10, windowMs = 60000) => {
  const requests = [];
  
  return (identifier = 'default') => {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    // Clean old requests
    const recentRequests = requests.filter(req => 
      req.timestamp > windowStart && req.identifier === identifier
    );
    
    if (recentRequests.length >= maxRequests) {
      return {
        allowed: false,
        retryAfter: Math.ceil((recentRequests[0].timestamp + windowMs - now) / 1000),
      };
    }
    
    // Add current request
    requests.push({ identifier, timestamp: now });
    
    // Keep only recent requests
    requests.splice(0, requests.length - maxRequests);
    
    return { allowed: true, retryAfter: 0 };
  };
};

// Export all utilities
export const inputSecurity = {
  sanitizeHtml,
  sanitizeSqlInput,
  validateEmail,
  sanitizeSearchQuery,
  validateNumeric,
  validateUrl,
  sanitizeTextInput,
  validatePassword,
  sanitizeFormData,
  validateSecurityHeaders,
  validateCSP,
  createRateLimiter,
};

export default inputSecurity;